<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      oaId: '',
      appId: '',
      appSecret: '',
      accessToken: '',
      refreshToken: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  validations: {
    inboxName: { required },
    oaId: { required },
    appId: { required },
    appSecret: { required },
    accessToken: { required },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const inbox = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName || 'Zalo OA',
          channel: {
            type: 'zalo_oa',
            oa_id: this.oaId,
            app_id: this.appId,
            app_secret: this.appSecret,
            access_token: this.accessToken,
            refresh_token: this.refreshToken,
          },
        });

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: inbox.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message || 'Failed to create Zalo OA channel. Please try again.'
        );
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      header-title="Zalo Official Account"
      header-content="Connect your Zalo OA to handle customer messages. Get your OA credentials from https://developers.zalo.me"
    />
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.inboxName.$error }">
          Inbox Name
          <input
            v-model="inboxName"
            type="text"
            placeholder="e.g. Penthouse Asia OA"
            @blur="v$.inboxName.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.oaId.$error }">
          OA ID
          <input
            v-model="oaId"
            type="text"
            placeholder="Your Zalo OA ID"
            @blur="v$.oaId.$touch"
          />
        </label>
        <p class="help-text">
          Find your OA ID in the Zalo OA management dashboard
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.appId.$error }">
          App ID
          <input
            v-model="appId"
            type="text"
            placeholder="Your Zalo App ID"
            @blur="v$.appId.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.appSecret.$error }">
          App Secret
          <input
            v-model="appSecret"
            type="password"
            placeholder="Your Zalo App Secret"
            @blur="v$.appSecret.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.accessToken.$error }">
          Access Token
          <input
            v-model="accessToken"
            type="password"
            placeholder="OA Access Token"
            @blur="v$.accessToken.$touch"
          />
        </label>
        <p class="help-text">
          Generate from Zalo Developer Portal → API Explorer
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label>
          Refresh Token (optional)
          <input
            v-model="refreshToken"
            type="password"
            placeholder="OA Refresh Token (for auto-renewal)"
          />
        </label>
        <p class="help-text">
          If provided, the system will auto-refresh tokens before expiry
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          label="Create Zalo OA Channel"
        />
      </div>
    </form>
  </div>
</template>
